import { useEffect, useState } from 'react';
import { api, unwrap, type ApiEnvelope } from './api';
import type { Company } from '@/types/client';

export async function getCompany(): Promise<Company> {
  return unwrap(api.get<ApiEnvelope<Company>>('/me/company'));
}

export async function patchCompany(patch: Partial<Pick<Company, 'companyName' | 'defaultVendor'>>): Promise<Company> {
  return unwrap(api.patch<ApiEnvelope<Company>>('/me/company', patch));
}

export async function uploadCompanyLogo(file: File): Promise<Company> {
  const fd = new FormData();
  fd.append('logo', file, file.name);
  return unwrap(api.post<ApiEnvelope<Company>>('/me/company/logo', fd));
}

export async function deleteCompanyLogo(): Promise<Company> {
  return unwrap(api.delete<ApiEnvelope<Company>>('/me/company/logo'));
}

export function companyLogoUrl(cacheBust?: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  const qs = cacheBust ? `?t=${encodeURIComponent(cacheBust)}` : '';
  return `${base}/me/company/logo${qs}`;
}

/** Fetch the company logo via the authed axios instance. Browsers don't
 *  attach Authorization to <img src>, so we pull the bytes ourselves and
 *  wrap them in a Blob URL. */
export async function fetchCompanyLogoBlob(): Promise<Blob> {
  const res = await api.get('/me/company/logo', { responseType: 'blob' });
  return res.data as Blob;
}

/** React hook returning a Blob URL for the user's company logo, refreshed
 *  whenever `cacheKey` changes (use any monotonically-increasing value like
 *  Date.now() after a successful upload). Returns null when the user has
 *  no logo yet. */
export function useCompanyLogoUrl(hasLogo: boolean | undefined, cacheKey: string | number): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!hasLogo) { setUrl(null); return undefined; }
    let revoked = false;
    let created: string | null = null;
    fetchCompanyLogoBlob().then((blob) => {
      if (revoked) return;
      created = URL.createObjectURL(blob);
      setUrl(created);
    }).catch(() => { if (!revoked) setUrl(null); });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [hasLogo, cacheKey]);
  return url;
}
